'use client';

import React, { FC } from 'react';
import { TypeLink_external } from '@/core/generated-types';
import { ChevronIcon, CrossIcon } from '../Icons';
import buildShopSubdomainUrl from '@/utils/shopSubdomain';
import classNames from 'classnames';
import useLocale from '@/hooks/useLocale';
import useFeature from '@/hooks/useFeature';
import { FeatureFlagValues } from '@/core/featureFlags';

export interface ExternalLinkProps {
  link: TypeLink_external;
  classes?: string; // Append to the default classes
  children?: React.ReactNode;
  hideArrow?: boolean;
  chevronClasses?: string;
  onClick?: React.MouseEventHandler<any> | undefined;
  testId?: string;
}

const ExternalLink: FC<ExternalLinkProps> = ({
  link: {
    sys: { id },
    fields: {
      linkText,
      linkUrl,
      featureLinkUrl,
      featureFlagName,
      blankTarget,
      noFollow,
      hasLeadingArrow,
      hasArrow: hasTrailingArrow,
      hasShopSubdomain,
      buttonStyle,
      shouldDisplayCrossIcon,
    },
  },
  classes = '',
  children,
  hideArrow,
  chevronClasses,
  onClick,
  testId,
}) => {
  const variation = useFeature(featureFlagName as keyof FeatureFlagValues);

  const shouldUseFeatureLinkUrl = variation === 'b';

  const url =
    shouldUseFeatureLinkUrl && featureLinkUrl ? featureLinkUrl : linkUrl;

  const locale = useLocale();
  const href = hasShopSubdomain ? buildShopSubdomainUrl(url, locale) : url;
  const isLinkStyle = buttonStyle === 'Link';

  return (
    <a
      key={id}
      href={href}
      className={`${classes} flex flex-row items-center gap-1.5`}
      data-testid={testId || 'link-externalLink'}
      rel={noFollow ? 'nofollow' : undefined}
      target={blankTarget ? '_blank' : undefined}
      onClick={onClick}
    >
      {hasLeadingArrow && !hideArrow && (
        <ChevronIcon
          color="indigo"
          direction="left"
          height={13}
          width={13}
          strokeWidth={3}
          classes={classNames('relative top-[1px]', chevronClasses)}
        />
      )}
      <div className="flex gap-2 items-center">
        {children || linkText}
        {shouldDisplayCrossIcon && (
          <CrossIcon
            classes={classNames({
              'order-first': isLinkStyle,
            })}
          />
        )}
      </div>
      {hasTrailingArrow && !hideArrow && (
        <ChevronIcon
          color="indigo"
          direction="right"
          height={13}
          width={13}
          strokeWidth={3}
          classes={classNames('relative top-[1px]', chevronClasses)}
        />
      )}
    </a>
  );
};

export default React.memo(ExternalLink);
